import { useMutation, useQuery } from '@tanstack/react-query'

import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'

import { queryClient } from '@tabeeb/services/queryClient'
import qs from 'qs'
import { AIQueryKeys } from '../queries'

const useFormControlTags = ({ formControlIds, enabled }: { formControlIds: number[]; enabled?: boolean }) => {
  const { aiServiceApi: api } = useApi()

  const queryKey = [AIQueryKeys.FormControlTags, formControlIds]

  const query = useQuery({
    queryKey,
    queryFn: () =>
      api.formControlTags.getByFormControlIdsAsync(
        { fcIds: formControlIds },
        {
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
        }
      ),
    cacheTime: Infinity,
    refetchInterval: false,
    enabled,
  })

  const attachTagToControlMutation = useMutation({
    mutationFn: async (request: { FormControlId: number; TagId?: number; Name?: string }) =>
      request.TagId
        ? api.formControlTags.postAsync({
            FormControlId: request.FormControlId,
            TagId: request.TagId,
            IsCreatedManually: false,
          })
        : api.formControlTags.addNewTagToFormControlAsync({
            FormControlId: request.FormControlId,
            Name: request.Name!,
          }),
    onSuccess: (response, request) => {
      queryClient.setQueryData<typeof query['data']>(queryKey, (prev) => {
        if (!prev) {
          return prev
        }

        return [...prev, response]
      })
    },
  })

  const detachTagFromControlMutation = useMutation({
    mutationFn: async ({ FormControlTagId }: { FormControlTagId: number }) =>
      api.formControlTags.deleteAsync(FormControlTagId),
    onSuccess: (_, request) => {
      queryClient.setQueryData<typeof query['data']>(queryKey, (prev) => {
        if (!prev) {
          return prev
        }

        return prev.filter((t) => t.FormControlTagId !== request.FormControlTagId)
      })
    },
  })

  return {
    loading: query.isLoading,
    onAttachTagToControl: attachTagToControlMutation.mutate,
    onDetachTagFromControl: detachTagFromControlMutation.mutate,
    formControlTags: query.data ?? [],
  }
}

export default useFormControlTags
